import React, { useCallback, useMemo, useState } from "react";
import { LocalizeContextProps, withLocalize } from "react-localize-redux";
import { Field, Link, makeStyles, tokens } from "@fluentui/react-components";
import { AddCircle16Filled } from "@fluentui/react-icons";
import { ProjectFilesDialog } from "../../dialogs/projectFiles/ProjectFilesDialog";
import { SmartFilingManagerFluent9 } from "../../../services/SmartFiling/SmartFilingManagerFluent9";
import { Logger } from "../../../services/Logger";
import { InvalidateCacheService } from "../../../services/NewformaApi/InvalidateCacheService";
import { OfficeWrapper } from "../../../services/OfficeWrapper";
import { IProjectsService } from "../../../services/NewformaApi/IProjectsService";
import { AttachmentItem } from "../../../models/shared/AttachmentList";
import { SharePointAuthServiceFactory } from "../../../services/SharePoint/SharePointAuthServiceFactory";

const useStyles = makeStyles({
    container: {
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        paddingTop: "12px",
    },
    linkContainer: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "6px",
        paddingTop: "12px",
        paddingBottom: "6px",
    },
    link: {
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        fontSize: tokens.fontSizeBase200,
        lineHeight: tokens.lineHeightBase200,
    },
});

export interface ProjectFilesSectionProps extends LocalizeContextProps {
    smartFilingManager: SmartFilingManagerFluent9;
    invalidateCacheService: InvalidateCacheService;
    logger: Logger;
    officeWrapper: OfficeWrapper;
    projectsService: IProjectsService;
    sharePointAuthServiceFactory?: SharePointAuthServiceFactory;
    selectedProjectAttachments: AttachmentItem[];
    onProjectAttachmentsUpdated: (attachments: AttachmentItem[]) => void;
    onOpenDialog?: () => void;
    existingAttachmentCount?: number;
    /** Attachments from other sections (local files, email attachments) that share the
     *  attachment panel. Their names/paths are added to the duplicate lookup so a project
     *  file cannot land at the same path as one of them (rule D5). */
    localAttachments?: AttachmentItem[];
}

export const ProjectFilesSection = withLocalize((props: ProjectFilesSectionProps) => {
    const styles = useStyles();
    const [isDialogOpen, setIsDialogOpen] = useState(false);

    // Mixed-format lookup set passed to ProjectFilesDialog (and through to the hook).
    // Three key shapes are intentional — see existingFileIds JSDoc on useProjectFolderSelection.
    // Keys keep their original casing; the hook lower-cases both sides so matching is
    // case-insensitive (rules D7/D8).
    const existingFileIds = useMemo(
        () =>
            new Set(
                [
                    // Local attachments: FileWithId.id is the dropped path ("test.txt" or
                    // "folderA\\file.txt"), which is exactly where it lands in the panel.
                    // Email attachments (AttachmentDetails) land flat, so key by name.
                    ...(props.localAttachments ?? []).map((a) => ("attachmentType" in a ? a.name : a.id)),
                    ...props.selectedProjectAttachments.flatMap((a) => {
                        const withPath = a as {
                            displayPath?: string;
                            addToParentFolder?: boolean;
                            projectFolderRelativePath?: string;
                        };
                        const keys: string[] = [];
                        // Keys are landing paths in the transfer (folder relative path + name), never
                        // the source apiPath: a file attached from inside folder A must not block A.
                        if (withPath.addToParentFolder === false && withPath.projectFolderRelativePath) {
                            // Rolled up into its level folder. Checked first: a folder-fetched file keeps
                            // its pre-roll-up displayPath, which is no longer where it lands.
                            keys.push(`${withPath.projectFolderRelativePath}\\${a.name}`);
                        } else if (withPath.displayPath) {
                            // Folder-fetched: displayPath is already projectFolderRelativePath\name.
                            keys.push(withPath.displayPath);
                        } else {
                            // Flat/individually-attached: key by BOTH NRN (same-project detection)
                            // and name (cross-project same-name detection).
                            keys.push(...([a.id, a.name].filter(Boolean) as string[]));
                        }
                        return keys;
                    }),
                ].filter(Boolean) as string[]
            ),
        [props.selectedProjectAttachments, props.localAttachments]
    );

    const handleOpenDialog = useCallback(() => {
        props.onOpenDialog?.();
        setIsDialogOpen(true);
    }, [props.onOpenDialog]);

    const handleCloseDialog = useCallback(() => {
        setIsDialogOpen(false);
    }, []);

    const handleAttach = useCallback(
        (newAttachments: AttachmentItem[]) => {
            props.onProjectAttachmentsUpdated([...props.selectedProjectAttachments, ...newAttachments]);
            setIsDialogOpen(false);
        },
        [props.onProjectAttachmentsUpdated, props.selectedProjectAttachments]
    );

    return (
        <div className={styles.container}>
            <Field
                label={props.translate("APP.FILE_TRANSFER.PROJECT_FILES_SECTION.LABEL").toString()}
                data-testid="project-files-field"
            >
                <div className={styles.linkContainer}>
                    <Link className={styles.link} onClick={handleOpenDialog} data-testid="select-project-files-link">
                        <AddCircle16Filled />
                        {props.translate("APP.FILE_TRANSFER.PROJECT_FILES_SECTION.SELECT_ATTACHMENTS").toString()}
                    </Link>
                </div>
            </Field>
            <ProjectFilesDialog
                isOpen={isDialogOpen}
                onClose={handleCloseDialog}
                onAttach={handleAttach}
                smartFilingManager={props.smartFilingManager}
                invalidateCacheService={props.invalidateCacheService}
                logger={props.logger}
                officeWrapper={props.officeWrapper}
                projectsService={props.projectsService}
                sharePointAuthServiceFactory={props.sharePointAuthServiceFactory}
                existingAttachmentCount={props.existingAttachmentCount ?? props.selectedProjectAttachments.length}
                existingFileIds={existingFileIds}
            />
        </div>
    );
});
